import {
  Flex,
  HEADER_HEIGHT,
  List,
  Typography,
  useSafeArea,
} from '@banhala/ui-kit';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { MarketHomeContentsKeys } from '../types/MarketHomeContents';
import { useMarketRoute } from '../../../hooks/useMarketRoute';
import { useMarketSno } from '../hooks/useMarketSno';
import { useMarketDetail } from '../queries/useMarketDetail';
import { MarketHomeHeader } from './MarketHomeHeader';
import { MarketHomeBanner } from './MarketHomeBanner';
import Animated, {
  useAnimatedScrollHandler,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated';
import { ScrollContext } from './MarketHomeBanner/context/ScrollContext';
import { MarketInfo } from './MarketInfo';
import { MarketCoupon } from './MarketCoupon';
import { MarketGoodsCarousel } from './MarketGoodsCarousel';
import { MarketCategories } from './MarketCategories';
import {
  marketGoodsCategoriesFilterAtom,
  type MarketGoodsListFilter,
  marketGoodsListFilterAtom,
} from '../states/GoodsListFilter.atom';
import { MarketGoodsList } from './MarketGoodsList';
import { useAtom, useSetAtom } from 'jotai';
import type { ListRef } from '@banhala/ui-kit';
import { isAndroid, isApp, isIOS, isWeb } from 'shared/src/utils';
import { useLogViewMarket } from '../hooks/useLogViewMarket';
import { MarketHomeScrollToTop } from './MarketHomeScrollToTop';
import { useScrollToGoodsList } from '../hooks/useScrollToGoodsList';
import { useReportTTFD } from '../hooks/useReportTTFD';
import { scheduleOnRN } from 'react-native-worklets';
import { useMarketGoodsCarousels } from '../hooks/useMarketGoodsCarousels';
import { DeliveryType } from 'shared/src/enums';
import { Metadata } from '../../../../../../shared/src/component';
import { getMetaImage, getMetaTitle } from '../utils/marketMetaData';
import { useMarketHomeGoods } from '../hooks/useMarketHomeGoods';

const AnimatedList = Animated.createAnimatedComponent(
  List<MarketHomeContentsKeys>,
);

// 앱과 웹의 차이를 반영한 스크롤 보정값
const SCROLL_CORRECTION_VALUE = isApp() && isIOS ? 72 : 42;

const SectionContainer: React.FC<{
  children: React.ReactNode;
  onChangeHeight: (height: number) => void;
}> = props => {
  const { onChangeHeight } = props;

  const [height, setHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      onChangeHeight(height);
    };

    updateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  return (
    <Flex
      onLayout={({ nativeEvent }) => {
        setHeight(nativeEvent.layout.height);
      }}
    >
      {props.children}
    </Flex>
  );
};

export const MarketHomeContents = () => {
  const listRef = useRef<ListRef<MarketHomeContentsKeys> | null>(null);

  const marketId = useMarketSno();
  const { params } = useMarketRoute<'MarketHome'>();
  const { data: marketDetail } = useMarketDetail(marketId);
  // useMarketGoodsCarousels의 응답값이 상품 리스트보다 느릴 때 스크롤이 2번 실행되는 이슈가 있어 응답 여부를 판단하도록 합니다.
  const { isFetched: isMarketGoodsCarouselsFetched } =
    useMarketGoodsCarousels(marketId);
  const scrollY = useSharedValue(0);
  const categoriesScrollX = useSharedValue(0);
  const { top, bottom } = useSafeArea();
  const isFastDelivery = useRef(
    params.extras?.delivery_type === DeliveryType.Fast ||
      params.delivery_type === DeliveryType.Fast,
  );
  const [filter, setFilter] = useAtom(marketGoodsListFilterAtom);

  const { getNextMarketHomeGoods } = useMarketHomeGoods(
    marketId,
    filter[marketId],
  );

  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y;
  });

  const [lastFilter, setLastFilter] = useState<
    MarketGoodsListFilter | undefined
  >();

  const marketContentList = useMemo<MarketHomeContentsKeys[]>(() => {
    if (!marketDetail) {
      return [];
    }
    return ['banner', 'market-info', 'coupon', 'carousel', 'category', 'goods'];
  }, [marketDetail]);

  const marketContentStickyIndices = useMemo(() => {
    return [marketContentList.findIndex(item => item === 'category')];
  }, [marketContentList]);

  const [sectionHeights, setSectionHeights] = useState<{
    [key: string]: number;
  }>({});

  const categoriesPositionY = useMemo(() => {
    return Object.keys(sectionHeights).reduce((acc, key) => {
      return acc + sectionHeights[key];
    }, 0);
  }, [sectionHeights]);
  const setCategoriesFilter = useSetAtom(marketGoodsCategoriesFilterAtom);

  useEffect(() => {
    if (
      isFastDelivery.current === false ||
      !isMarketGoodsCarouselsFetched ||
      categoriesPositionY <= 0
    )
      return;

    setTimeout(() => {
      setFilter(prev => ({
        ...prev,
        [marketId]: {
          ...prev[marketId],
          deliveryType: DeliveryType.Fast,
        },
      }));

      isFastDelivery.current = false;
    }, 10);
  }, [isMarketGoodsCarouselsFetched, setFilter, marketId, categoriesPositionY]);

  const [showCategoriesStickyView, setShowCategoriesStickyView] =
    useState(false);

  useEffect(() => {
    setCategoriesFilter(prev => ({
      ...prev,
      [marketId]: {
        showSearch: false,
        keyword: '',
      },
    }));
  }, [marketId, setCategoriesFilter]);

  useEffect(() => {
    if (marketContentList.length > 0) {
      if (
        lastFilter &&
        JSON.stringify(lastFilter) === JSON.stringify(filter[marketId])
      ) {
        return;
      }
      setLastFilter(filter[marketId]);
      // listRef.current?.scrollToItem({ animated: true, item: 'category' });
      listRef.current?.scrollToOffset({
        animated: true,
        offset: categoriesPositionY - SCROLL_CORRECTION_VALUE,
      });
    }
  }, [marketId, filter, lastFilter, marketContentList, categoriesPositionY]);

  useDerivedValue(() => {
    if (!scrollY) return;

    if (categoriesPositionY === 0) return;

    const targetHeight = HEADER_HEIGHT + top;

    if (showCategoriesStickyView) {
      if (scrollY.value < categoriesPositionY - targetHeight) {
        scheduleOnRN(setShowCategoriesStickyView, false);
      }
    } else {
      if (scrollY.value >= categoriesPositionY - targetHeight) {
        scheduleOnRN(setShowCategoriesStickyView, true);
      }
    }
  });

  useLogViewMarket();

  useScrollToGoodsList(listRef, categoriesPositionY);
  useReportTTFD();

  // const testGroupB = useExperimentGroup({
  //   sno: 'EXP1509',
  //   targetGroups: ['B'],
  // });

  return (
    <ScrollContext.Provider value={{ scrollY, categoriesScrollX, listRef }}>
      <Flex flex={1}>
        <Metadata
          title={getMetaTitle(marketDetail)}
          imgSrc={getMetaImage(marketDetail)}
          path={`/markets/${params.marketId}`}
          isCanonical
        />
        {/*{testGroupB && (*/}
        {/*  <MarketHomeHeader*/}
        {/*    marketId={marketId}*/}
        {/*    positionY={categoriesPositionY}*/}
        {/*    showCategoriesStickyView={showCategoriesStickyView}*/}
        {/*  />*/}
        {/*)}*/}
        <AnimatedList
          ref={listRef}
          onEndReachedThreshold={2}
          data={marketContentList}
          style={{ flex: 1 }}
          stickyHeaderIndices={marketContentStickyIndices}
          onScroll={scrollHandler}
          disableVirtualization={isWeb}
          scrollEventThrottle={1}
          windowSize={isAndroid ? 2 : undefined}
          contentContainerStyle={{
            paddingBottom: bottom,
          }}
          renderItem={({ item }) => {
            if (item === 'banner') {
              return (
                <SectionContainer
                  onChangeHeight={height => {
                    setSectionHeights(prev => ({ ...prev, [item]: height }));
                  }}
                >
                  <MarketHomeBanner />
                </SectionContainer>
              );
            }
            if (item === 'market-info') {
              return (
                <SectionContainer
                  onChangeHeight={height => {
                    setSectionHeights(prev => ({ ...prev, [item]: height }));
                  }}
                >
                  <MarketInfo />
                </SectionContainer>
              );
            }
            if (item === 'coupon') {
              return (
                <SectionContainer
                  onChangeHeight={height => {
                    setSectionHeights(prev => ({ ...prev, [item]: height }));
                  }}
                >
                  <MarketCoupon />
                </SectionContainer>
              );
            }

            if (item === 'carousel') {
              return (
                <SectionContainer
                  onChangeHeight={height => {
                    setSectionHeights(prev => ({ ...prev, [item]: height }));
                  }}
                >
                  <MarketGoodsCarousel />
                </SectionContainer>
              );
            }

            if (item === 'category') {
              return (
                <MarketCategories
                  positionY={categoriesPositionY}
                  showCategoriesStickyView={showCategoriesStickyView}
                />
              );
            }

            if (item === 'goods') {
              return <MarketGoodsList />;
            }

            return (
              <Flex backgroundColor={'bg_primary'} height={300}>
                <Typography type={'h3'}>{item}</Typography>
              </Flex>
            );
          }}
          onEndReached={getNextMarketHomeGoods}
        />

        <MarketHomeHeader
          positionY={categoriesPositionY}
          showCategoriesStickyView={showCategoriesStickyView}
        />
        <MarketHomeScrollToTop />
      </Flex>
    </ScrollContext.Provider>
  );
};
